import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Button, Select, formatSelectOptions, LinkList } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { personService } from '../../../services'
import { useNetworkDialog } from '../hooks/useNetworkDialog'
import { relationTypeClass, metaFor, projectPipeline, projectNetworking, NETWORKING_TYPE_LABELS } from '../helpers'
import { RecruitmentPipeline } from '../components/RecruitmentPipeline'
import { FreeText } from '../components/FreeText'
import { appName } from '../appConfig'
import { getStoryRole } from '../../../helpers'

const CONTACT_FIELDS = [
  ['E-post', 'person_email'],
  ['LinkedIn', 'person_linkedin'],
  ['Telefon', 'person_phone'],
  ['Företag', 'company_name'],
  ['Titel', 'person_title'],
]

// Popup opened by the Relationer header's ✎ icon. Owns its own local copy of
// the pill/available-type lists, seeded at open time, same reasoning as
// LinkList's editor: the modal's Content is a static closure that never
// sees the host page's own re-fetch, so each add/remove updates local state
// directly rather than relying on the parent to hand back fresh props.
const RelationsEditor = ({ initialPills, initialAvailable, onAdd, onRemove }) => {
  const [pills, setPills] = useState(initialPills)
  const [available, setAvailable] = useState(initialAvailable)

  const add = (relationTypeId) => {
    const type = available.find(t => t.relation_type_id === Number(relationTypeId))
    if (!type) return
    onAdd(type.relation_type_id).then(() => {
      setPills(prev => [...prev, { ...type, removable: true, className: relationTypeClass[type.relation_type_code] || 'pill' }])
      setAvailable(prev => prev.filter(t => t.relation_type_id !== type.relation_type_id))
    }).catch(() => {})
  }

  const remove = (pill) => {
    onRemove(pill.relation_type_id).then(() => {
      setPills(prev => prev.filter(p => p.relation_type_id !== pill.relation_type_id))
      setAvailable(prev => [...prev, { relation_type_id: pill.relation_type_id, relation_type_name: pill.relation_type_name, relation_type_code: pill.relation_type_code }])
    }).catch(() => {})
  }

  return (
    <div className="network-form">
      <div className="pill-list">
        {pills.map(p => (
          <span key={`${p.relation_type_code}-${p.removable}`} className={p.className}>
            {p.relation_type_name}
            {p.removable &&
              <button className="button-icon" title="Ta bort" onClick={() => remove(p)}>✕</button>
            }
          </span>
        ))}
      </div>
      {available.length > 0 &&
        <Select
          name="addingRelationInPopup"
          value=""
          placeholder="+ Lägg till relation"
          options={formatSelectOptions({ options: available, optionValue: 'relation_type_id', optionLabel: 'relation_type_name' })}
          onChange={({ value }) => add(value)}
        />
      }
    </div>
  )
}

// Kayenta-kontakt counterpart of RelationsEditor above — same local-state-copy reasoning
// (the modal's Content is a static closure that never sees the host page's re-fetch).
// Every contact is removable: unlike relations, there is no derived variant here.
const KayentaContactsEditor = ({ initialContacts, initialAvailable, onAdd, onRemove }) => {
  const [contacts, setContacts] = useState(initialContacts)
  const [available, setAvailable] = useState(initialAvailable)

  const add = (userId) => {
    const user = available.find(u => String(u.user_id) === String(userId))
    if (!user) return
    onAdd(user.user_id).then(() => {
      setContacts(prev => [...prev, { user_id: user.user_id, user_fullname: user.name, user_email: user.email }])
      setAvailable(prev => prev.filter(u => u.user_id !== user.user_id))
    }).catch(() => {})
  }

  const remove = (contact) => {
    onRemove(contact.user_id).then(() => {
      setContacts(prev => prev.filter(c => c.user_id !== contact.user_id))
      setAvailable(prev => [...prev, { user_id: contact.user_id, name: contact.user_fullname, email: contact.user_email }])
    }).catch(() => {})
  }

  return (
    <div className="network-form">
      <div className="network-kc-row">
        {contacts.length === 0
          ? <span className="network-kc-empty">Ingen Kayenta-kontakt vald</span>
          : contacts.map(c => (
            <span key={c.user_id} className="network-kc-chip">
              {c.user_fullname}
              <button type="button" className="network-kc-chip-remove" title="Ta bort" onClick={() => remove(c)}>✕</button>
            </span>
          ))
        }
      </div>
      {available.length > 0 &&
        <Select
          name="addingKayentaContactInPopup"
          value=""
          placeholder="+ Lägg till Kayenta-kontakt"
          options={formatSelectOptions({ options: available, optionValue: 'user_id', optionLabel: 'name', tooltip: 'email' })}
          onChange={({ value }) => add(value)}
        />
      }
    </div>
  )
}

const PersonDetail = () => {
  const navigate = useNavigate()
  const { uuid } = useParams()
  const { showMessage } = useMessage()
  const { showModalDialog, hideModalDialog } = useModalDialog()
  const { showPersonDialog, showDeletePersonDialog, showNetworkingDialog, showDeleteNetworkingDialog } = useNetworkDialog()

  const [person, setPerson] = useState(null)
  const [relationTypes, setRelationTypes] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [eventView, setEventView] = useState('current') // 'current' (Nuläge) | 'all' (Alla händelser)
  // Nätverkande rows collapse purpose/result/next_step into an expandable
  // section (same treatment as the cross-person Nätverkande overview), toggled
  // by source_ref.
  const [expandedNetworking, setExpandedNetworking] = useState(new Set())

  const toggleNetworkingExpand = (ref) => {
    setExpandedNetworking(prev => {
      const next = new Set(prev)
      next.has(ref) ? next.delete(ref) : next.add(ref)
      return next
    })
  }

  const fetchPerson = () => {
    setLoading(true)
    personService.getByUuid(uuid)
      .then(res => { setPerson(res.person); setLoading(false) })
      .catch(() => { showMessage('Kunde inte ladda person', 'error'); setLoading(false) })
  }

  useEffect(() => {
    fetchPerson()
    personService.getRelationTypes().then(res => setRelationTypes(res.relationTypes || []))
    personService.getUsers().then(res => setUsers(res.users || [])).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid])

  const addRelation = (relationTypeId) => {
    if (!relationTypeId) return Promise.resolve()
    return personService.addRelation(uuid, relationTypeId)
      .then(() => fetchPerson())
      .catch(err => { showMessage(err.message || 'Kunde inte lägga till relation', 'error'); throw err })
  }

  const removeRelation = (relationTypeId) => {
    return personService.removeRelation(uuid, relationTypeId)
      .then(() => fetchPerson())
      .catch(err => { showMessage(err.message || 'Kunde inte ta bort relation', 'error'); throw err })
  }

  const openRelationsEditor = (pills, availableRelationTypes) => {
    showModalDialog({
      title: 'Redigera relationer',
      Content: () => (
        <RelationsEditor
          initialPills={pills.filter(p => p.removable)}
          initialAvailable={availableRelationTypes}
          onAdd={addRelation}
          onRemove={removeRelation}
        />
      ),
      submitLabel: null,
      cancelLabel: 'Stäng',
      onCancel: hideModalDialog,
    })
  }

  const addKayentaContact = (userId) => {
    if (!userId) return Promise.resolve()
    return personService.addKayentaContact(uuid, userId)
      .then(() => fetchPerson())
      .catch(err => { showMessage(err.message || 'Kunde inte lägga till Kayenta-kontakt', 'error'); throw err })
  }

  const removeKayentaContact = (userId) => {
    return personService.removeKayentaContact(uuid, userId)
      .then(() => fetchPerson())
      .catch(err => { showMessage(err.message || 'Kunde inte ta bort Kayenta-kontakt', 'error'); throw err })
  }

  const openKayentaContactsEditor = (contacts, available) => {
    showModalDialog({
      title: 'Redigera Kayenta-kontakter',
      Content: () => (
        <KayentaContactsEditor
          initialContacts={contacts}
          initialAvailable={available}
          onAdd={addKayentaContact}
          onRemove={removeKayentaContact}
        />
      ),
      submitLabel: null,
      cancelLabel: 'Stäng',
      onCancel: hideModalDialog,
    })
  }

  const deletePerson = () => {
    showDeletePersonDialog(person, async () => {
      await personService.destroy(uuid)
        .then(() => { hideModalDialog(); showMessage('Borttagen'); navigate(`/${appName}/persons`) })
        .catch(err => showMessage(err.message || 'Kunde inte ta bort', 'error'))
    })
  }

  if (loading || !person) return <div className="network-page"><p className="empty-text">Laddar…</p></div>

  const curatedTypeIds = new Set((person.relations || []).map(r => r.relation_type_id))
  const derivedTypeIds = new Set((person.derived_relations || []).map(r => r.relation_type_id))
  const availableRelationTypes = relationTypes.filter(rt => !curatedTypeIds.has(rt.relation_type_id) && !derivedTypeIds.has(rt.relation_type_id))

  const linkedUserIds = new Set((person.kayenta_contacts || []).map(c => String(c.user_id)))
  const availableUsers = users.filter(u => !linkedUserIds.has(String(u.user_id)))

  // Curated (removable, solid) first, then derived (non-removable, dashed) —
  // matches PersonList's pill styling so a person reads the same way in the
  // list and on their own page.
  const pills = [
    ...(person.relations || []).map(r => ({ ...r, removable: true, className: relationTypeClass[r.relation_type_code] || 'pill' })),
    ...(person.derived_relations || []).map(r => ({ ...r, removable: false, className: (relationTypeClass[r.relation_type_code] || 'pill') + ' pill-dashed' })),
  ]

  // Event log. "Nuläge" = current state per source (recruitment as a pipeline,
  // plus live-derived Stories/Kundcase involvement). "Alla händelser" = the raw
  // append-only log. Recruitment folds into one pipeline block (projectPipeline);
  // Event & mingel is hidden until that app emits events (Phase 3).
  const events = person.events || []
  const pipeline = projectPipeline(events)
  const networking = projectNetworking(events)
  const stories = person.stories || []
  const clientCases = person.clientCases || []
  const hasCurrent = !!pipeline || stories.length > 0 || clientCases.length > 0 || networking.length > 0

  const fmtDate = (ts) => (ts ? String(ts).slice(0, 10) : '')

  const renderEventRow = (ev, { showDate } = {}) => {
    const meta = metaFor(ev.event_type)
    const r = meta.render(ev.payload || {})
    return (
      <div className="network-inv-row" key={ev.person_event_id}>
        <div className="network-inv-row-top">
          {showDate && <span className="network-event-date">{fmtDate(ev.occurred_at)}</span>}
          <div className="network-event-text">
            <span className="network-inv-title">{meta.icon} {r.title}</span>
            {r.subtitle && <span className="network-event-sub">{r.subtitle}</span>}
          </div>
        </div>
        {r.link && <Link className="network-inv-link" to={r.link.to}>{r.link.label} →</Link>}
      </div>
    )
  }

  return (
    <div className="network-page network-detail-page">

      <div className="network-detail-header">
        <h2>{person.person_name}</h2>
        <div className="network-detail-header-actions">
          <Button value="Redigera" className="button" onClick={() => showPersonDialog(person, () => fetchPerson())} />
          {!person.is_internal &&
            <Button value="Ta bort" className="button network-danger-button" onClick={deletePerson} />
          }
        </div>
      </div>

      {person.is_internal &&
        <div className="network-internal-banner">🔒 Kontaktfält synkas från medarbetarregistret</div>
      }

      <div className="network-detail-layout">
        <div className="network-detail-left network-detail-card">

          {CONTACT_FIELDS.map(([label, field]) => (
            <div className="network-contact-row" key={field}>
              <span className="network-contact-label">{label}</span>
              <span className="network-contact-value">{person[field] || '—'}</span>
            </div>
          ))}

          <div className="network-detail-section">
            <div className="link-list-header">
              <h3 className="network-sec-title">Relationer</h3>
              <button type="button" className="button-icon link-list-edit-button" title="Redigera relationer" onClick={() => openRelationsEditor(pills, availableRelationTypes)}>✎</button>
            </div>
            <div className="pill-list">
              {pills.map(r => (
                // Derived relations (talare/deltagare/alumni/rekrytering) never
                // carry a relation_type_id — only curated (person_relation)
                // rows do — so the key must be code+removable, not id, or two
                // derived pills (or a curated+derived pair of the same code,
                // e.g. someone curated-tagged talare who also later spoke at a
                // story) collide on `undefined`.
                <span key={`${r.relation_type_code}-${r.removable}`} className={r.className}>
                  {r.relation_type_name}
                </span>
              ))}
            </div>
          </div>

          {!person.is_internal &&
            <div className="network-detail-section">
              <div className="link-list-header">
                <h3 className="network-sec-title">Kayenta-kontakter</h3>
                <button type="button" className="button-icon link-list-edit-button" title="Redigera Kayenta-kontakter" onClick={() => openKayentaContactsEditor(person.kayenta_contacts || [], availableUsers)}>✎</button>
              </div>
              <div className="network-kc-row">
                {(person.kayenta_contacts || []).length === 0
                  ? <span className="network-kc-empty">Saknas — varje extern person bör ha minst en</span>
                  : (person.kayenta_contacts || []).map(c => (
                    <span key={c.user_id} className="network-kc-chip">{c.user_fullname}</span>
                  ))
                }
              </div>
            </div>
          }

          <div className="network-detail-section">
            <LinkList
              title="Länkar"
              titleClassName="network-sec-title"
              links={person.links || []}
              onAdd={(link) => personService.addLink(uuid, link).then(res => { fetchPerson(); return res.link })}
              onRemove={(link) => personService.removeLink(uuid, link.person_link_id).then(fetchPerson)}
              sourceLabels={{ clients: 'Kunder' }}
            />
          </div>

          <div className="network-detail-section">
            <h3 className="network-sec-title">Anteckningar</h3>
            <p className="network-notes-text">{person.person_notes || '—'}</p>
          </div>

        </div>

        <div className="network-detail-right">
          <div className="network-eventlog-header">
            <h3 className="network-sec-title">Aktivitet</h3>
            <div className="network-event-toggle">
              <button
                type="button"
                className={eventView === 'current' ? 'active' : ''}
                onClick={() => setEventView('current')}
              >Nuläge</button>
              <button
                type="button"
                className={eventView === 'all' ? 'active' : ''}
                onClick={() => setEventView('all')}
              >Alla händelser</button>
            </div>
          </div>

          {eventView === 'current' ? (
            <>
              {/* Rekrytering — one candidate-pipeline block (Kunder interest today;
                  candidate-app stages drop in later via projectPipeline). */}
              {pipeline &&
                <div className="network-detail-section">
                  <h3 className="network-sec-title">Rekrytering</h3>
                  <RecruitmentPipeline pipeline={pipeline} />
                </div>
              }

              {/* Stories — live-derived involvement (event-backed in a later PR) */}
              {stories.length > 0 &&
                <div className="network-detail-section">
                  <h3 className="network-sec-title">Stories <span className="network-sec-count">· {stories.length}</span></h3>
                  {stories.map(s => {
                    const role = getStoryRole(s.role)
                    return (
                      <div className="network-inv-row" key={s.story_id}>
                        <div className="network-inv-row-top">
                          <span className="network-inv-title">🎤 {s.story_slug}</span>
                          <span className={relationTypeClass[role.code] || 'pill'}>{role.label}</span>
                        </div>
                        <Link className="network-inv-link" to={`/kayenta_stories/${s.story_slug}`}>Visa story →</Link>
                      </div>
                    )
                  })}
                </div>
              }

              {/* Nätverkande — networking events logged here, mirrored to Teams.
                  Always shown (with a register affordance) since this is where
                  entries are created; folds create/edit/delete via projectNetworking. */}
              <div className="network-detail-section">
                <div className="link-list-header">
                  <h3 className="network-sec-title">
                    Nätverkande{networking.length > 0 && <span className="network-sec-count"> · {networking.length}</span>}
                  </h3>
                  <button type="button" className="button-icon link-list-edit-button" title="Registrera nätverkande" onClick={() => showNetworkingDialog(person, null, () => fetchPerson())}>+</button>
                </div>
                {networking.length === 0
                  ? <p className="empty-text">Inga nätverkanden ännu.</p>
                  : networking.map(n => {
                    const isOpen = expandedNetworking.has(n.source_ref)
                    // "Nätverkare" (author) is always visible — only the free-text
                    // purpose/result/next_step collapse into the expandable section.
                    const hasDetails = Boolean(n.purpose || n.result || n.next_step)
                    return (
                      <div className="network-inv-row network-networking-row" key={n.source_ref}>
                        <div className="network-networking-row-top">
                          <span className="network-inv-title">
                            🤝 {n.role || null}{n.role && n.company_name ? ' · ' : ''}{n.company_name || null}
                          </span>
                          <span className="pill network-networking-type">{NETWORKING_TYPE_LABELS[n.type] || n.type}</span>
                          <span className="network-event-date">{fmtDate(n.occurred_at)}</span>
                          <div className="network-inv-row-actions">
                            <button type="button" className="button-icon" title="Redigera" onClick={() => showNetworkingDialog(person, n, () => fetchPerson())}>✎</button>
                            <button type="button" className="button-icon" title="Ta bort" onClick={() => showDeleteNetworkingDialog(n, () => fetchPerson())}>🗑</button>
                          </div>
                        </div>
                        {n.author &&
                          <div className="network-event-text">
                            <span className="network-event-sub">Nätverkare: {n.author}</span>
                          </div>
                        }
                        {hasDetails &&
                          <button
                            type="button"
                            className="network-expand-toggle"
                            onClick={() => toggleNetworkingExpand(n.source_ref)}
                          >
                            <span className={`network-expand-chevron${isOpen ? ' open' : ''}`}>▸</span>
                            {isOpen ? 'Dölj detaljer' : 'Visa detaljer'}
                          </button>
                        }
                        {isOpen && hasDetails &&
                          <div className="network-event-text network-networking-details">
                            {n.purpose && <div className="network-event-fact"><span className="network-event-fact-label">Syfte</span><FreeText text={n.purpose} className="network-event-sub network-event-freetext" /></div>}
                            {n.result && <div className="network-event-fact"><span className="network-event-fact-label">Resultat</span><FreeText text={n.result} className="network-event-sub network-event-freetext" /></div>}
                            {n.next_step && <div className="network-event-fact"><span className="network-event-fact-label">Nästa steg</span><FreeText text={n.next_step} className="network-event-sub network-event-freetext" /></div>}
                          </div>
                        }
                      </div>
                    )
                  })
                }
              </div>


              {/* Kundcase — live-derived involvement */}
              {clientCases.length > 0 &&
                <div className="network-detail-section">
                  <h3 className="network-sec-title">Kundcase <span className="network-sec-count">· {clientCases.length}</span></h3>
                  {clientCases.map(c => (
                    <div className="network-inv-row" key={c.client_uuid}>
                      <div className="network-inv-row-top">
                        <span className="network-inv-title">🏢 {c.client_slug}</span>
                        {c.client_contact_role && <span className="pill pill-green">{c.client_contact_role}</span>}
                      </div>
                      <Link className="network-inv-link" to={`/clients/${c.client_slug}`}>Visa i Kunder →</Link>
                    </div>
                  ))}
                </div>
              }

              {!hasCurrent && <p className="empty-text">Inga aktiviteter ännu.</p>}
            </>
          ) : (
            <div className="network-detail-section">
              {events.length > 0
                ? events.map(ev => renderEventRow(ev, { showDate: true }))
                : <p className="empty-text">Inga händelser ännu.</p>
              }
            </div>
          )}
        </div>
      </div>

    </div>
  )
}

export default PersonDetail
